import {
	CSSObject,
	DrawerProps,
	StackProps,
	styled,
	Theme,
} from '@mui/material';
import { ReactComponent as ChevronLeftIcon } from 'assett/icons/chevron_left_icon.svg';
import { ReactComponent as ChevronRightIcon } from 'assett/icons/chevron_right_icon.svg';
import useDevice from 'hooks/DesktopDevice.hook';
import { useLogout } from 'hooks/login.hook';
import mobile from 'is-mobile';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Drawer, List, Stack } from 'styles';
import { TRANSLATION_KEYS } from 'utils/constants';
import { Dashboard_Sections, Home, Logout } from 'utils/links';
import { useTranslation } from 'utils/translations';
import ListItemButtonCustom from './ListItemButtonCustom.component';

const DRAWER_WIDTH = '300px';
const openedMixin = (theme: Theme, desktop: boolean): CSSObject => ({
	width: DRAWER_WIDTH,
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	boxShadow: '3px 3px 12px 0px rgba(0, 0, 0, 0.05)',
	overflowX: 'hidden',
});

const closedMixin = (theme: Theme, desktop: boolean): CSSObject => ({
	transition: theme.transitions.create('width', {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	overflowX: 'hidden',
	boxShadow: '3px 3px 12px 0px rgba(0, 0, 0, 0.05)',
	'&.MuiStack-root>.MuiStack-root>.MuiDivider-root': {
		width: 32,
	},
	width: `calc(${theme.spacing(8.5)} + 1px)`,
	[theme.breakpoints.up('lg')]: {
		width: `calc(${theme.spacing(10)} + 1px)`,
	},
});

type DrawerStyleProps = DrawerProps & {
	desktop: boolean;
};

const StyledDrawer = styled(Drawer, {
	shouldForwardProp: prop => !['open', 'desktop'].includes(String(prop)),
})<DrawerStyleProps>(({ theme, open, desktop }) => ({
	width: DRAWER_WIDTH,
	flexShrink: 0,
	whiteSpace: 'nowrap',
	boxSizing: 'border-box',
	...(open && {
		...openedMixin(theme, desktop),
		'& .MuiDrawer-paper.left-sidebar': openedMixin(theme, desktop),
	}),
	...(!open && {
		...closedMixin(theme, desktop),
		'& .MuiDrawer-paper.left-sidebar': closedMixin(theme, desktop),
	}),
}));

type StackPropsStyled = StackProps & {
	open: boolean;
	desktop: boolean;
};

const StyledButtonSidebar = styled(Stack, {
	shouldForwardProp: prop => !['open', 'desktop'].includes(String(prop)),
})<StackPropsStyled>(({ theme, open, desktop }) => ({
	width: 24,
	height: '100vh',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginLeft: -1,
	'& .Sidebar-Button': {
		boxShadow: '9px 1px 15px 0px rgba(0, 0, 0, 0.05)',
		borderTopRightRadius: 15,
		borderBottomRightRadius: 15,
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		background: 'white',
		height: 129,
		zIndex: 10000,
		marginLeft: !open && !desktop ? -1 : 0,
		cursor: 'pointer',
	},
}));

const Sidebar = () => {
	const { t: tClientLabels } = useTranslation('', {
		keyPrefix: TRANSLATION_KEYS.CLIENT_LABELS,
	});
	const { isDesktop } = useDevice();
	const navigate = useNavigate();
	const handleLogout = useLogout();
	const [open, setOpen] = useState<boolean>(false);
	const [maxPageHeight] = useState(window.innerHeight);

	const isMobile = mobile({ tablet: true, featureDetect: true });

	return (
		<>
			<Box
				sx={{
					position: 'absolute',
					top: 0,
					left: 0,
					width: open ? '100%' : '0%',
					height: `${window.innerHeight}px`,
					background: 'rgba(50, 50, 50, 0.25)',
					opacity: open ? 0.6 : 0,
					zIndex: 99,
				}}
			/>
			<Box position={'fixed'} left={0} zIndex={100}>
				<Box sx={{ position: 'fixed', display: 'flex' }} role="presentation">
					<StyledDrawer
						desktop={isDesktop}
						variant="permanent"
						open={open}
						PaperProps={{
							className: 'left-sidebar',
							sx: {
								borderRight: 0,
								borderTopRightRadius: 10,
								borderBottomRightRadius: 10,
								position: 'static',
							},
						}}
					>
						<Stack
							height={`${isMobile ? maxPageHeight : window.innerHeight}px`}
							justifyContent={'space-between'}
						>
							<Stack
								flexGrow={1}
								gap={5}
								sx={{ px: !!isDesktop ? 3 : 2.25, overflow: 'hidden' }}
							>
								<List sx={{ rowGap: 5 }}>
									{Home(tClientLabels).map(action => (
										<ListItemButtonCustom
											key={action.id}
											open={open}
											action={action}
											onClick={() => navigate(action.link)}
											sx={{ mt: 6.5 }}
										/>
									))}
								</List>
								<List sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
									{Dashboard_Sections(tClientLabels, false)
										.filter(e => !!e.show)
										.map(action => (
											<ListItemButtonCustom
												key={action.id}
												open={open}
												action={action}
												onClick={() => navigate(action.link)}
											/>
										))}
								</List>
							</Stack>
							<Stack flexGrow={0} sx={{ px: !!isDesktop ? 3 : 2.25 }}>
								<List>
									{Logout(tClientLabels).map(action => (
										<ListItemButtonCustom
											key={action.id}
											open={open}
											action={action}
											onClick={handleLogout}
											sx={{ my: 5 }}
										/>
									))}
								</List>
							</Stack>
						</Stack>
					</StyledDrawer>
					<StyledButtonSidebar open={open} desktop={isDesktop}>
						<span className="Sidebar-Button" onClick={() => setOpen(!open)}>
							{open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
						</span>
					</StyledButtonSidebar>
				</Box>
			</Box>
		</>
	);
};

export default Sidebar;
